import { useCallback, useMemo, useState } from "react";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Heading,
  Paragraph,
  Flex,
  Card,
  CardHeader,
  CardContent,
  Accordion,
  Switch,
  Checkbox,
  Chip,
  Icon,
  Icons,
  Spin,
  useToast,
} from "@ir/ui";
import { useGlobalDataLookup } from "@src/presentation/providers/GlobalDataLookupProvider";
import { useRolesPermissions } from "@src/presentation/providers/RolesPermissionsProvider";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { UpdateRolePermissionsDTO } from "@src/domain/dto/rolePermissions";
import { UpdateRolePermissionsUseCase } from "@src/application/useCases/rolePermissions";
import { RolePermissionsRepository } from "@src/infrastructure/repositories/rolePermissions";
import type { FeatureLookupItem } from "@src/domain/dto/features";
import { useAdminAuth } from "@src/presentation/hooks/auth";

interface RolePermissionsTabsProps {
  orientation: "horizontal" | "vertical";
  keyboardActivation?: "automatic" | "manual";
}

interface UpdateRolePermissionPayload {
  roleId: number;
  featureId: number;
  isActive: boolean;
  permissionId?: number;
}

export const RolePermissionsTabs = ({
  orientation,
  keyboardActivation,
}: RolePermissionsTabsProps) => {
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const { error: errorToast, success: successToast } = useToast();
  const { featuresLookup, featuresLookupLoading } = useGlobalDataLookup();
  const { rolePermissions, rolePermissionsLoading, applyRolePermissionUpdate } =
    useRolesPermissions();
  const adminAuth = useAdminAuth();
  const features = featuresLookup ?? [];
  const roles = rolePermissions ?? [];

  const setPending = useCallback((key: string, pending: boolean) => {
    setPendingKeys((prev) => {
      const next = new Set(prev);
      if (pending) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const buildApplyPayload = useCallback(
    (
      payload: UpdateRolePermissionPayload,
      lookupFeature: FeatureLookupItem,
    ) => ({
      ...payload,
      featurePermissions: lookupFeature.featurePermissions ?? [],
      featureMeta: {
        featureName: lookupFeature.featureName,
        featureKey: lookupFeature.featureKey,
        featureDescription: lookupFeature.featureDescription,
        featureIsMainMenu: lookupFeature.featureIsMainMenu,
        featureDisplayName: lookupFeature.featureDisplayName,
      },
    }),
    [],
  );

  const getPendingKey = useCallback((payload: UpdateRolePermissionPayload) => {
    const permissionKey =
      payload.permissionId == null ? "feature" : String(payload.permissionId);
    return `${payload.roleId}:${payload.featureId}:${permissionKey}`;
  }, []);

  const updateRolePermission = useCallback(
    async (
      payload: UpdateRolePermissionPayload,
      lookupFeature: FeatureLookupItem,
    ) => {
      const key = getPendingKey(payload);
      setPending(key, true);
      const applyPayload = buildApplyPayload(payload, lookupFeature);
      applyRolePermissionUpdate(applyPayload);
      try {
        const validated = UpdateRolePermissionsDTO.validate(payload);
        if (!validated.success) {
          console.error(
            "UpdateRolePermissions validation failed",
            validated.error,
          );
          throw new Error("UpdateRolePermissions validation failed");
        }

        const dto = new UpdateRolePermissionsDTO(validated.data);
        await new UpdateRolePermissionsUseCase(
          new RolePermissionsRepository(
            GraphQlAdapter.getInstance().getClient(),
          ),
        ).execute(dto);
        try {
          await adminAuth.refreshPermissions();
        } catch (refreshError) {
          console.error(
            "Role permissions updated but failed to refresh current user permissions",
            refreshError,
          );
        }
        successToast({
          content: "Role permissions updated",
          icon: <Icon name={Icons.CheckCircle} />,
        });
      } catch (error) {
        console.error("Failed to update role permissions", error);
        applyRolePermissionUpdate(
          buildApplyPayload(
            { ...payload, isActive: !payload.isActive },
            lookupFeature,
          ),
        );
        errorToast({
          content: "Failed to update role permissions",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setPending(key, false);
      }
    },
    [
      adminAuth,
      applyRolePermissionUpdate,
      buildApplyPayload,
      errorToast,
      getPendingKey,
      setPending,
      successToast,
    ],
  );

  const handleFeatureSwitchChange = useCallback(
    (roleId: number, feature: FeatureLookupItem, isActive: boolean) => {
      updateRolePermission(
        {
          roleId,
          featureId: feature.featureId,
          isActive,
        },
        feature,
      );
    },
    [updateRolePermission],
  );

  const handlePermissionCheckboxChange = useCallback(
    (
      roleId: number,
      feature: FeatureLookupItem,
      permissionId: number,
      checked: boolean,
    ) => {
      updateRolePermission(
        {
          roleId,
          featureId: feature.featureId,
          permissionId,
          isActive: checked,
        },
        feature,
      );
    },
    [updateRolePermission],
  );

  const totalPermissionsCount = useMemo(
    () =>
      features.reduce(
        (total, feature) => total + (feature.featurePermissions?.length ?? 0),
        0,
      ),
    [features],
  );

  return (
    <ErrorBoundary>
      <Flex className="role-permissions-main">
        {featuresLookupLoading || rolePermissionsLoading ? (
          <Flex
            align="center"
            justifyContent="center"
            style={{ height: "calc(100vh - 5rem)", width: "100%" }}
          >
            <Spin spinning tip="Loading..." />
          </Flex>
        ) : (
          <Tabs
            orientation={orientation}
            keyboardActivation={keyboardActivation}
          >
            <TabList className="mb-3">
              {roles.map((role) => {
                const activePermissionCount = features.reduce(
                  (total, feature) => {
                    const roleFeature = role.roleFeatures.find(
                      (item) =>
                        String(item.featureId) === String(feature.featureId),
                    );
                    const rolePermissionIds = new Set(
                      (roleFeature?.featurePermissions ?? [])
                        .filter((permission) => permission.isActive)
                        .map((permission) => String(permission.id)),
                    );
                    return (
                      total +
                      (feature.featurePermissions ?? []).filter((permission) =>
                        rolePermissionIds.has(String(permission.id)),
                      ).length
                    );
                  },
                  0,
                );

                return (
                  <Tab
                    key={role.roleId}
                    htmlTitle={role.roleName}
                    id={`tab-${role.roleId}`}
                    className="flex-1"
                  >
                    <Flex direction="column" align="start" className="flex-1">
                      <Paragraph className="text-grey-700 font-bold m-0 text-sm">
                        {role.roleName}
                      </Paragraph>
                      <Paragraph className="text-grey-500 font-regular m-0 text-xs">
                        {activePermissionCount} Active Permissions
                      </Paragraph>
                    </Flex>
                    <Flex>
                      <Icon name={Icons.ChevronRight} />
                    </Flex>
                  </Tab>
                );
              })}
            </TabList>
            {roles.map((role) => {
              const activePermissionsCount = features.reduce(
                (total, feature) => {
                  const roleFeature = role.roleFeatures.find(
                    (item) =>
                      String(item.featureId) === String(feature.featureId),
                  );
                  return total + (roleFeature?.featurePermissions?.length ?? 0);
                },
                0,
              );

              return (
                <TabPanel key={role.roleId} id={`tab-${role.roleId}`}>
                  <Card>
                    <CardHeader className="bg-white border-color-grey-50 mb-3 tab-sub-header">
                      <Flex
                        direction="row"
                        align="center"
                        justifyContent="space-between"
                      >
                        <Flex direction="column" align="start">
                          <Heading
                            className="text-grey-700 font-bold m-0"
                            level={6}
                          >
                            {role.roleName}
                          </Heading>
                          <Flex direction="row" align="end">
                            <Paragraph className="text-grey-500 font-regular m-0 text-xs">
                              {activePermissionsCount} / {totalPermissionsCount}{" "}
                              Active Permissions
                            </Paragraph>
                          </Flex>
                        </Flex>
                      </Flex>
                    </CardHeader>

                    <CardContent className="p-0">
                      <Accordion
                        allowsMultipleExpanded
                        items={features.map((feature) => {
                          const permissions = feature.featurePermissions ?? [];
                          const roleFeature = role.roleFeatures.find(
                            (item) =>
                              String(item.featureId) ===
                              String(feature.featureId),
                          );
                          const featureIsSelected = Boolean(
                            roleFeature?.featureIsActive,
                          );
                          const activePermissionIds = new Set(
                            (roleFeature?.featurePermissions ?? [])
                              .filter((permission) => permission.isActive)
                              .map((permission) => String(permission.id)),
                          );
                          const activePermissionsCount = permissions.filter(
                            (permission) =>
                              activePermissionIds.has(String(permission.id)),
                          ).length;

                          const featureSwitchPending = pendingKeys.has(
                            `${role.roleId}:${feature.featureId}:feature`,
                          );

                          return {
                            key: String(feature.featureId),
                            title: (
                              <Flex
                                className="Feature-row"
                                direction="row"
                                align="center"
                                justifyContent="space-between"
                                style={{ flex: 1 }}
                              >
                                <Flex direction="row" align="start" gap={1}>
                                  <Chip
                                    size="small"
                                    shape="square"
                                    color={
                                      featureIsSelected ? "success" : "grey"
                                    }
                                    style={{ marginLeft: "-1px" }}
                                    label={
                                      feature.featureDisplayName ||
                                      feature.featureName
                                    }
                                  />
                                  <Paragraph className="text-grey-500 font-regular m-0 text-xs pt-1">
                                    {activePermissionsCount} /{" "}
                                    {permissions.length} Active Permissions
                                  </Paragraph>
                                </Flex>
                                <Flex align="center" gap={0.5} className="pe-1">
                                  {featureSwitchPending && (
                                    <Spin
                                      size="small"
                                      spinning
                                      style={{ maxHeight: "12px !important" }}
                                    />
                                  )}
                                  <Switch
                                    size="small"
                                    isSelected={featureIsSelected}
                                    onChange={handleFeatureSwitchChange.bind(
                                      null,
                                      role.roleId,
                                      feature,
                                    )}
                                    isDisabled={featureSwitchPending}
                                  />
                                </Flex>
                              </Flex>
                            ),
                            content: (
                              <Flex direction="column" align="stretch">
                                {permissions.map((permission) => (
                                  <Flex
                                    key={`${feature.featureId}-${permission.id}`}
                                    className="Permission-row px-3"
                                    direction="row"
                                    align="center"
                                    justifyContent="space-between"
                                  >
                                    <Flex direction="column" align="start">
                                      <Paragraph className="text-grey-500 font-bold m-0 text-xs">
                                        {permission.name}
                                      </Paragraph>
                                      <Paragraph
                                        className="text-grey-500 font-regular m-0"
                                        style={{ fontSize: "0.65rem" }}
                                      >
                                        {permission.description ||
                                          "No description"}
                                      </Paragraph>
                                    </Flex>
                                    <Flex>
                                      <Flex
                                        direction="row"
                                        align="end"
                                        justifyContent="space-between"
                                        gap={1}
                                      >
                                        <Chip
                                          size="small"
                                          borderless
                                          color={
                                            activePermissionIds.has(
                                              String(permission.id),
                                            )
                                              ? "success"
                                              : "grey"
                                          }
                                          label={permission.permissionKey}
                                        />

                                        {pendingKeys.has(
                                          `${role.roleId}:${feature.featureId}:${String(permission.id)}`,
                                        ) && (
                                          <Flex
                                            align="center"
                                            justifyContent="center"
                                            style={{ maxHeight: "1rem" }}
                                          >
                                            <Spin
                                              size="small"
                                              spinning
                                              style={{
                                                maxHeight: "12px !important",
                                              }}
                                            />
                                          </Flex>
                                        )}

                                        <Checkbox
                                          size="small"
                                          color="success"
                                          checked={activePermissionIds.has(
                                            String(permission.id),
                                          )}
                                          isDisabled={
                                            !featureIsSelected ||
                                            featureSwitchPending ||
                                            pendingKeys.has(
                                              `${role.roleId}:${feature.featureId}:${String(permission.id)}`,
                                            )
                                          }
                                          onChange={handlePermissionCheckboxChange.bind(
                                            null,
                                            role.roleId,
                                            feature,
                                            permission.id,
                                          )}
                                        />
                                      </Flex>
                                    </Flex>
                                  </Flex>
                                ))}
                              </Flex>
                            ),
                          };
                        })}
                      />
                    </CardContent>
                  </Card>
                </TabPanel>
              );
            })}
          </Tabs>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
